import React from 'react';
import { LogOut, Image, MapPin, Package, Menu, X, FileText, Newspaper, Calendar } from 'lucide-react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useState, useEffect } from 'react';

const AdminNavbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', checkMobile);
    checkMobile();

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/kh-admin/login');
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <div className="bg-[#2A382A] border-b border-white/10 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link 
            to="/kh-admin"
            aria-label="Go to admin dashboard"
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
          >
            <img src="/assets/logo-inversed-new.webp" alt="SF Admin" className="w-8 h-8" />
            <span className="font-barrio text-xl text-white">SF ADMIN</span>
          </Link>

          {/* Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/kh-admin/tours"
              aria-label="Manage tours"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                location.pathname === '/kh-admin/tours'
                  ? 'bg-white/10 text-white'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              <MapPin className="w-4 h-4" />
              Tours
            </Link>
            <Link
              to="/kh-admin/bookings"
              aria-label="Manage bookings"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                location.pathname === '/kh-admin/bookings'
                  ? 'bg-white/10 text-white'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              <Calendar className="w-4 h-4" />
              Bookings
            </Link>
            <Link
              to="/kh-admin/items"
              aria-label="Manage tour items"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                location.pathname === '/kh-admin/items'
                  ? 'bg-white/10 text-white'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              <Package className="w-4 h-4" />
              Items
            </Link>
            <Link
              to="/kh-admin/news"
              aria-label="Manage news posts"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                location.pathname === '/kh-admin/news'
                  ? 'bg-white/10 text-white'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              <Newspaper className="w-4 h-4" />
              News
            </Link>
            <Link
              to="/kh-admin/scientific-work"
              aria-label="Manage scientific publications"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                location.pathname === '/kh-admin/scientific-work'
                  ? 'bg-white/10 text-white'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              <FileText className="w-4 h-4" />
              Scientific Work
            </Link>
            <Link
              to="/kh-admin/gallery"
              aria-label="Manage image gallery"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                location.pathname === '/kh-admin/gallery'
                  ? 'bg-white/10 text-white'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              <Image className="w-4 h-4" />
              Gallery
            </Link>
          </div>
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            {isMenuOpen ? (
              <X className="w-6 h-6 text-white" />
            ) : (
              <Menu className="w-6 h-6 text-white" />
            )}
          </button>

          {/* Logout Button */}
          <button 
            className="hidden md:flex items-center gap-2 px-4 py-2 text-white hover:bg-white/10 rounded-lg transition-colors"
            onClick={handleLogout}
            aria-label="Logout from admin panel"
          >
            <LogOut className="w-4 h-4" />
            <span>Logout</span>
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden transition-all duration-300 ease-in-out overflow-hidden ${
            isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="py-4 space-y-2">
            <Link
              to="/kh-admin/tours"
              onClick={closeMenu}
              className={`block px-4 py-2 rounded-lg transition-colors ${
                location.pathname === '/kh-admin/tours'
                  ? 'bg-white/10 text-white'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Tours
              </div>
            </Link>
            <Link
              to="/kh-admin/bookings"
              onClick={closeMenu}
              className={`block px-4 py-2 rounded-lg transition-colors ${
                location.pathname === '/kh-admin/bookings'
                  ? 'bg-white/10 text-white'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Bookings
              </div>
            </Link>
            <Link
              to="/kh-admin/items"
              onClick={closeMenu}
              className={`block px-4 py-2 rounded-lg transition-colors ${
                location.pathname === '/kh-admin/items'
                  ? 'bg-white/10 text-white'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4" />
                Items
              </div>
            </Link>
            <Link
              to="/kh-admin/news"
              onClick={closeMenu}
              className={`block px-4 py-2 rounded-lg transition-colors ${
                location.pathname === '/kh-admin/news'
                  ? 'bg-white/10 text-white'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              <div className="flex items-center gap-2">
                <Newspaper className="w-4 h-4" />
                News
              </div>
            </Link>
            <Link
              to="/kh-admin/scientific-work"
              onClick={closeMenu}
              className={`block px-4 py-2 rounded-lg transition-colors ${
                location.pathname === '/kh-admin/scientific-work'
                  ? 'bg-white/10 text-white'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Scientific Work
              </div>
            </Link>
            <Link
              to="/kh-admin/gallery"
              onClick={closeMenu}
              className={`block px-4 py-2 rounded-lg transition-colors ${
                location.pathname === '/kh-admin/gallery'
                  ? 'bg-white/10 text-white'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              <div className="flex items-center gap-2">
                <Image className="w-4 h-4" />
                Gallery
              </div>
            </Link>
            <button
              onClick={() => {
                closeMenu();
                handleLogout();
              }}
              className="w-full text-left px-4 py-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <div className="flex items-center gap-2">
                <LogOut className="w-4 h-4" />
                Logout
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminNavbar;